import { Box, List, ListItem, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { createPortal } from "react-dom";
import IconLoader from "@/features/Common/components/IconLoader";
import DefentSheet from "../DefentSheet";
import HealthScoreIndicator from "../Common/HealthScoreIndicator";
import { convertPxToRem } from "@/utils";
import { useGetAboutYourHealthScoreData } from "./hooks/useGetAboutYourHealthScoreData";
import theme from "@/theme";
import { useHealthScoreRatingColour } from "../Common/hooks/useHealthScoreRatingColour";
import { useTranslation } from "react-i18next";

interface AboutYourHealthScoreProps {
  healthScore: number;
}

export default function AboutYourHealthScore({
  healthScore,
}: AboutYourHealthScoreProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();
  const { healthScoreRatingColour } = useHealthScoreRatingColour(healthScore);
  const { aboutYourHealthScoreData } = useGetAboutYourHealthScoreData();

  function handleToggleModal() {
    setIsOpen(!isOpen);
  }

  return (
    <>
      {/* Modal Trigger */}
      <Stack
        flexDirection="row"
        alignItems="center"
        gap={1}
        onClick={handleToggleModal}
        sx={{ cursor: "pointer" }}
      >
        <IconLoader icon="InformationSymbolInCircleIcon" color="info" />
        <Typography
          variant="body2"
          color="info"
          component="span"
          fontSize={convertPxToRem(14)}
          fontWeight={700}
          sx={{ textDecoration: "underline" }}
        >
          {t("HealthscorePage.learnMore")}
        </Typography>
      </Stack>

      {/* Modal */}
      {createPortal(
        <DefentSheet
          expanded={isOpen}
          onClose={handleToggleModal}
          onExpandedChange={setIsOpen}
        >
          <Stack
            width="100%"
            spacing={4}
            aria-label={t("HealthscorePage.learnMore.modal.label")}
          >
            <Stack
              direction="row"
              position="absolute"
              left={convertPxToRem(16)}
              top={convertPxToRem(32)}
            >
              <Typography
                fontSize={convertPxToRem(38)}
                fontWeight={700}
                mr={1}
                color={healthScoreRatingColour}
                aria-label={t("HealthscorePage.learnMore.modal.label.score")}
              >
                {healthScore}
              </Typography>
              <Typography
                fontSize={convertPxToRem(38)}
                fontWeight={300}
                color={theme.palette.neutral[300]}
              >
                / 100
              </Typography>
            </Stack>

            <Box pt={6}>
              <HealthScoreIndicator healthScore={healthScore} />
            </Box>

            <List
              sx={{
                "&&": { marginTop: convertPxToRem(24) },
              }}
            >
              {aboutYourHealthScoreData.map((item, index) => (
                <ListItem
                  aria-label={t("HealthscorePage.learnMore.modal.label.list")}
                  key={index}
                  sx={{
                    px: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                  }}
                >
                  <Box display="flex" flexDirection="row">
                    <Typography
                      color={item.titleColor}
                      mr={1}
                      fontWeight={600}
                      fontSize={convertPxToRem(14)}
                      aria-label={t(
                        "HealthscorePage.learnMore.modal.label.title"
                      )}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      fontWeight={600}
                      fontSize={convertPxToRem(14)}
                      aria-label={t(
                        "HealthscorePage.learnMore.modal.label.range"
                      )}
                    >
                      {item.range}
                    </Typography>
                  </Box>
                  <Typography
                    fontWeight={400}
                    fontSize={convertPxToRem(14)}
                    component="p"
                    aria-label={t(
                      "HealthscorePage.learnMore.modal.label.description"
                    )}
                  >
                    {item.description}
                  </Typography>
                </ListItem>
              ))}
            </List>
          </Stack>
        </DefentSheet>,
        document.body
      )}
    </>
  );
}
